import default_img from "./assets/default_img.png";
import default_avatar from "./assets/default_avatar.png";

import {httpsCallable} from "firebase/functions";
import {auth, clearFirestoreCache, db, functions, storage} from "./firebase";
import {
	deleteUser,
	EmailAuthProvider,
	applyActionCode,
	isSignInWithEmailLink,
	reauthenticateWithCredential,
	sendEmailVerification,
	sendSignInLinkToEmail,
	signInWithEmailLink,
	signOut,
	updateProfile as updateAuthProfile,
	verifyBeforeUpdateEmail
} from "firebase/auth";
import {deleteObject, getDownloadURL, listAll, ref, uploadBytes} from "firebase/storage";
import {
	addDoc,
	collection,
	deleteDoc,
	doc,
	getDoc,
	getDocs,
	onSnapshot,
	query,
	updateDoc,
	where,
	writeBatch
} from "firebase/firestore";
import {
	getFormattedValue,
	getProfileIdsFromEntity,
	MAX_CATEGORY_NAME_LENGTH,
	MAX_LINK_NAME_LENGTH,
	MAX_LINK_URL_LENGTH,
	MAX_PROFILE_NAME_LENGTH,
	normalizeProfileId,
	toFiniteNumber,
	validateCategoryName,
	validateFieldLength,
	validateProfileName,
	VIRTUAL_ALL_PROFILE_ID
} from "./tools";
import {
	buildAvatarStoragePath,
	buildLinkImageStoragePath,
	canRemoveAvatarUrl,
	getExtensionFromPath,
	getStoragePathFromDownloadUrl,
	getUserAvatarPath,
	getUserLinkImagesPath,
	getUserStorageRoot,
	GLOBAL_DEFAULT_AVATAR_PATH,
	GLOBAL_DEFAULT_LINK_IMAGE_PATH,
	isGlobalDefaultAvatarPath,
	isLegacyAvatarPath,
	isUserScopedAvatarPath,
	needsAvatarMigration,
	needsLinkImageMigration
} from "./storagePaths";
import {
	isSupportedAvatarImageFormat,
	isSupportedLinkImageFormat,
	optimizeAvatarImage,
	optimizeLinkImage
} from "./imageUpload";

export const BUNDLED_DEFAULT_LINK_IMAGE = default_img;
export const BUNDLED_DEFAULT_AVATAR = default_avatar;

const EMULATOR_PROJECT_ID = 'demo-test';
const RESERVED_PROFILE_NAME = "ALL";

export const AUTH_EMAIL_PURPOSES = {
	signIn: 'signIn',
	emailChangeReauth: 'emailChangeReauth',
	accountDeletion: 'accountDeletion',
	emailChangeConfirm: 'emailChangeConfirm',
	emailChangeAlert: 'emailChangeAlert',
	emailVerification: 'emailVerification'
};

export const AUTH_ACTION_MODES = {
	signIn: 'signIn',
	verifyAndChangeEmail: 'verifyAndChangeEmail',
	verifyEmail: 'verifyEmail',
	recoverEmail: 'recoverEmail'
};

export const EMAIL_FOR_SIGNIN_STORAGE_KEY = 'linkspace.emailForSignIn';
export const PENDING_EMAIL_CHANGE_STORAGE_KEY = 'linkspace.pendingEmailChange';
export const PENDING_ACCOUNT_DELETION_STORAGE_KEY = 'linkspace.pendingAccountDeletion';

let emailLinkSignInInFlight = null;

export const shouldUseShortSignInLinks = () => (
	process.env.REACT_APP_USE_SHORT_SIGN_IN_LINKS !== 'false'
	&& process.env.REACT_APP_USE_FIREBASE_EMULATOR !== 'true'
);

const shouldUseCustomAuthEmails = () => shouldUseShortSignInLinks();

const refreshAuthToken = async () => {
	const user = auth.currentUser;
	if (!user?.getIdToken) return;
	await user.getIdToken(true);
};

const shouldDeliverSignInLinkViaCloud = (purpose) => (
	shouldUseCustomAuthEmails()
	|| (process.env.REACT_APP_USE_FIREBASE_EMULATOR !== 'true' && purpose !== AUTH_EMAIL_PURPOSES.signIn)
);

export const getShortSignInTokenFromHref = (href) => {
	if (!href) return null;
	try {
		const url = new URL(href);
		const match = url.pathname.match(/^\/auth\/s\/([a-f0-9]{32})$/i);
		return match ? match[1].toLowerCase() : null;
	} catch {
		return null;
	}
};

export const isShortSignInUrl = (href = globalThis.window?.location?.href) => (
	Boolean(getShortSignInTokenFromHref(href))
);

export const isExtensionShortSignInUrl = (href = globalThis.window?.location?.href) => {
	if (!isShortSignInUrl(href)) return false;
	try {
		return new URL(href).searchParams.get('lsExt') === '1';
	} catch {
		return false;
	}
};

export const buildSignInEmailLinkHref = (oobCode, {extension = false, mode = AUTH_ACTION_MODES.signIn} = {}) => {
	if (globalThis.window === undefined) return '';
	const apiKey = process.env.REACT_APP_FIREBASE_API_KEY ?? '';
	const params = new URLSearchParams({
		apiKey,
		oobCode,
		mode,
		lang: 'en'
	});
	if (extension) {
		params.set('lsExt', '1');
	}
	return `${globalThis.window.location.origin}/?${params.toString()}`;
};

const exchangeShortSignInToken = async (shortId) => {
	const exchange = httpsCallable(functions, 'exchangeSignInToken');
	const {data} = await exchange({shortId});
	if (!data?.ok || !data?.email || !data?.oobCode) {
		throw new Error('invalid-token-response');
	}
	return data;
};

const peekShortSignInToken = async (shortId) => {
	const peek = httpsCallable(functions, 'peekSignInToken');
	const {data} = await peek({shortId});
	if (!data?.ok || !data?.purpose) {
		throw new Error('invalid-token-response');
	}
	return data;
};

const completeEmailChangeViaToken = async (shortId, addError) => {
	const user = auth.currentUser;
	const fail = (errorText) => {
		addError(errorText);
		return {ok: false, needsEmail: false, errorText};
	};
	if (!user) {
		return fail('Open this link in the same browser where you are signed in to LinkSpace.');
	}
	try {
		await refreshAuthToken();
		const request = httpsCallable(functions, 'completeEmailChangeFromToken');
		await request({shortId});
		// Admin SDK changed the email server-side; the local session is stale until re-sign-in.
		try {
			await signOut(auth);
		} catch {}
		stripEmailLinkQueryFromCurrentUrl();
		clearPendingEmailChange();
		return {ok: true, needsEmail: false, action: 'emailChange'};
	} catch (error) {
		const code = String(error?.code ?? '');
		const message = String(error?.message ?? '');
		if (code === 'functions/unauthenticated') {
			return fail('Open this link in the same browser where you are signed in to LinkSpace.');
		}
		if (code === 'functions/not-found' || code === 'functions/failed-precondition') {
			return fail('This link is no longer valid. Request a new one and try again.');
		}
		if (code === 'functions/invalid-argument') {
			return fail('This link is invalid. Request a new one.');
		}
		if (message.includes('requires-recent-login')) {
			return fail('For security, sign in again on this browser, then reopen the confirmation link.');
		}
		return fail('Could not complete your email change. Please try again.');
	}
};

const revertEmailChangeViaToken = async (shortId, addError) => {
	try {
		const request = httpsCallable(functions, 'revertEmailChangeFromToken');
		await request({shortId});
		if (auth.currentUser) {
			try {
				await signOut(auth);
			} catch {}
		}
		stripEmailLinkQueryFromCurrentUrl();
		return {ok: true, needsEmail: false, action: 'emailRevert'};
	} catch (error) {
		const code = String(error?.code ?? '');
		if (code === 'functions/not-found' || code === 'functions/failed-precondition') {
			addError('This link is no longer valid or the email change was already reversed.');
		} else if (code === 'functions/invalid-argument') {
			addError('This link is invalid.');
		} else {
			addError('Could not reset your sign-in email. Please try again.');
		}
		return {ok: false, needsEmail: false};
	}
};

const mapRequestSignInLinkError = (error, addError) => {
	const code = String(error?.code ?? '');
	if (code === 'functions/invalid-argument') {
		addError('Invalid email address');
	} else if (code === 'functions/resource-exhausted') {
		addError('Too many attempts. Please try again later.');
	} else if (code === 'functions/failed-precondition') {
		const details = String(error?.message ?? '');
		if (details === 'unverified') {
			addError('Please verify your email on the website before signing in.');
		} else if (details === 'no_account') {
			addError('No LinkSpace account found for this email.');
		} else if (details === 'ineligible') {
			addError('This account cannot sign in from the extension yet.');
		} else if (details === 'Email link sign-in is not enabled.') {
			addError('Firebase Auth is not configured for this project/app. Check API key, Auth provider setup, and authorized domains.');
		} else {
			addError('Could not send sign-in email. Please try again.');
		}
	} else if (code === 'functions/unavailable') {
		addError('Could not send sign-in email right now. Please try again.');
	} else {
		addError('Could not send sign-in email. Please try again.');
	}
};

const deliverSignInLink = async (email, addError, {extension = false, purpose = AUTH_EMAIL_PURPOSES.signIn} = {}) => {
	if (globalThis.window !== undefined) {
		globalThis.window.localStorage.setItem(EMAIL_FOR_SIGNIN_STORAGE_KEY, email);
	}
	if (!shouldDeliverSignInLinkViaCloud(purpose)) {
		try {
			const actionCodeSettings = extension
				? {url: `${globalThis.window.location.origin}/?lsExt=1`, handleCodeInApp: true}
				: buildEmailLinkActionCodeSettings();
			await sendSignInLinkToEmail(auth, email, actionCodeSettings);
			return true;
		} catch (error) {
			if (globalThis.window !== undefined) {
				globalThis.window.localStorage.removeItem(EMAIL_FOR_SIGNIN_STORAGE_KEY);
			}

			const code = String(error?.code ?? '');
			if (code === 'auth/invalid-email') {
				addError('Invalid email address');
			} else if (code === 'auth/configuration-not-found') {
				addError('Firebase Auth is not configured for this project/app. Check API key, Auth provider setup, and authorized domains.');
			} else if (code === 'auth/too-many-requests') {
				addError('Too many attempts. Please try again later.');
			} else {
				addError('Could not send sign-in email. Please try again.');
			}
			return false;
		}
	}
	try {
		if (purpose === AUTH_EMAIL_PURPOSES.accountDeletion) {
			await refreshAuthToken();
			const request = httpsCallable(functions, 'requestAccountDeletionLink');
			await request({});
			return true;
		}
		if (purpose === AUTH_EMAIL_PURPOSES.emailChangeReauth) {
			await refreshAuthToken();
			const request = httpsCallable(functions, 'requestEmailChangeReauthLink');
			await request({});
			return true;
		}
		const request = httpsCallable(functions, 'requestSignInLink');
		await request({email, extension});
		return true;
	} catch (error) {
		if (globalThis.window !== undefined) {
			globalThis.window.localStorage.removeItem(EMAIL_FOR_SIGNIN_STORAGE_KEY);
		}
		mapRequestSignInLinkError(error, addError);
		return false;
	}
};

const mapEmailChangeLinkError = (error, addError) => {
	const code = String(error?.code ?? '');
	if (code === 'functions/unauthenticated') {
		addError('You must be logged in to change your email.');
	} else if (code === 'functions/invalid-argument') {
		const message = String(error?.message ?? '');
		if (message.includes('different from current')) {
			addError('New email must be different from current email.');
		} else {
			addError('Invalid email address');
		}
	} else if (code === 'functions/failed-precondition' && String(error?.message ?? '').includes('already in use')) {
		addError('Email is already in use');
	} else if (code === 'functions/resource-exhausted') {
		addError('Too many attempts. Please try again later.');
	} else {
		addError('Could not send confirmation email. Please try again.');
	}
};

const deliverEmailChangeConfirmation = async (newEmail, addError) => {
	if (!shouldUseCustomAuthEmails()) {
		await verifyBeforeUpdateEmail(auth.currentUser, newEmail, buildEmailUpdateActionCodeSettings());
		return {ok: true};
	}
	try {
		await refreshAuthToken();
		const request = httpsCallable(functions, 'requestEmailChangeLink');
		await request({newEmail});
		return {ok: true};
	} catch (error) {
		const code = String(error?.code ?? '');
		const message = String(error?.message ?? '');
		if (code === 'functions/failed-precondition' && message.includes('requires-recent-login')) {
			return {ok: false, reason: 'requires-recent-login'};
		}
		mapEmailChangeLinkError(error, addError);
		return {ok: false};
	}
};

const deliverEmailVerification = async (addError) => {
	if (!shouldUseCustomAuthEmails()) {
		await sendEmailVerification(auth.currentUser);
		return true;
	}
	try {
		await refreshAuthToken();
		const request = httpsCallable(functions, 'requestEmailVerificationLink');
		await request({});
		return true;
	} catch (error) {
		const code = String(error?.code ?? '');
		if (code === 'functions/unauthenticated') {
			addError('You must be logged in to resend verification email.');
		} else if (code === 'functions/resource-exhausted') {
			addError('Too many attempts. Please try again later.');
		} else {
			addError('Could not send verification email. Please try again.');
		}
		return false;
	}
};

const readPendingEmailChange = () => {
	if (globalThis.window === undefined) return null;
	try {
		const raw = globalThis.window.localStorage.getItem(PENDING_EMAIL_CHANGE_STORAGE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		const uid = String(parsed?.uid ?? '').trim();
		const nextEmail = String(parsed?.nextEmail ?? '').trim();
		if (!uid || !nextEmail) return null;
		return {uid, nextEmail};
	} catch {
		return null;
	}
};

const writePendingEmailChange = (uid, nextEmail) => {
	if (globalThis.window === undefined) return;
	globalThis.window.localStorage.setItem(
		PENDING_EMAIL_CHANGE_STORAGE_KEY,
		JSON.stringify({uid, nextEmail})
	);
};

const clearPendingEmailChange = () => {
	if (globalThis.window === undefined) return;
	globalThis.window.localStorage.removeItem(PENDING_EMAIL_CHANGE_STORAGE_KEY);
};

const readPendingAccountDeletion = () => {
	if (globalThis.window === undefined) return null;
	try {
		const raw = globalThis.window.localStorage.getItem(PENDING_ACCOUNT_DELETION_STORAGE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		const uid = String(parsed?.uid ?? '').trim();
		if (!uid) return null;
		return {uid};
	} catch {
		return null;
	}
};

const writePendingAccountDeletion = (uid) => {
	if (globalThis.window === undefined) return;
	globalThis.window.localStorage.setItem(
		PENDING_ACCOUNT_DELETION_STORAGE_KEY,
		JSON.stringify({uid})
	);
};

const clearPendingAccountDeletion = () => {
	if (globalThis.window === undefined) return;
	globalThis.window.localStorage.removeItem(PENDING_ACCOUNT_DELETION_STORAGE_KEY);
};

const getEmailLinkContinueUrl = () => {
	if (globalThis.window === undefined) return '';
	return `${globalThis.window.location.origin}/`;
};

const buildEmailLinkActionCodeSettings = () => ({
	url: getEmailLinkContinueUrl(),
	handleCodeInApp: true
});

const buildEmailUpdateActionCodeSettings = () => ({
	url: getEmailLinkContinueUrl(),
	handleCodeInApp: false
});

const stripEmailLinkQueryFromCurrentUrl = () => {
	if (globalThis.window === undefined) return;
	globalThis.window.history.replaceState({}, document.title, '/');
};

const sanitizeIdxPerProfile = (idxPerProfile) => {
	const normalizedIdxPerProfile = {};
	if (!idxPerProfile || typeof idxPerProfile !== "object") return normalizedIdxPerProfile;

	Object.entries(idxPerProfile).forEach(([profileId, idx]) => {
		const normalizedProfileId = normalizeProfileId(profileId);
		if (!normalizedProfileId) return;
		normalizedIdxPerProfile[normalizedProfileId] = toFiniteNumber(idx);
	});

	return normalizedIdxPerProfile;
};

const buildCategoryPayload = (categoryObject) => {
	const profileIds = Array.from(new Set(getProfileIdsFromEntity(categoryObject)));
	const idxPerProfile = sanitizeIdxPerProfile(categoryObject?.idxPerProfile);
	delete idxPerProfile[VIRTUAL_ALL_PROFILE_ID];

	const categoryName = String(categoryObject?.category ?? "").trim();
	return {
		category: getFormattedValue(categoryName),
		idx: toFiniteNumber(categoryObject?.idx),
		profileIds,
		idxPerProfile
	};
};

const toArray = (querySnapshot) => querySnapshot.docs.map((snapshot) => ({
	id: snapshot.id,
	...snapshot.data(),
}));

const chunkArray = (items, chunkSize) => {
	const chunks = [];
	for (let index = 0; index < items.length; index += chunkSize) {
		chunks.push(items.slice(index, index + chunkSize));
	}
	return chunks;
};

const getCurrentUserUid = (addError, action) => {
	const uid = auth.currentUser?.uid;
	if (!uid && process.env.NODE_ENV === 'test') {
		return 'test-uid';
	}
	if (!uid) {
		addError(`You must be logged in to ${action}.`);
		return null;
	}
	return uid;
};

export const isDestructiveCleanupAllowed = () => (
	process.env.REACT_APP_USE_FIREBASE_EMULATOR === 'true'
	&& process.env.REACT_APP_ALLOW_DB_CLEANUP === 'true'
	&& process.env.REACT_APP_FIREBASE_PROJECT_ID === EMULATOR_PROJECT_ID
);

export const isCurrentUrlEmailSignInLink = () => (
	globalThis.window !== undefined && isSignInWithEmailLink(auth, globalThis.window.location.href)
);

export const hasExtensionEmailLinkQueryParams = (href) => {
	if (!href) return false;
	try {
		const url = new URL(href);
		return url.searchParams.get('lsExt') === '1'
			&& Boolean(url.searchParams.get('oobCode'))
			&& url.searchParams.get('mode') === 'signIn'
			&& Boolean(url.searchParams.get('apiKey'));
	} catch {
		return false;
	}
};

export const isExtensionEmailLinkUrl = () => (
	globalThis.window !== undefined
	&& hasExtensionEmailLinkQueryParams(globalThis.window.location.href)
);

export const isEmailLinkLandingUrl = () => (
	isCurrentUrlEmailSignInLink() || isExtensionEmailLinkUrl() || isShortSignInUrl()
);

export const clearEmailLinkUrlIfPresent = () => {
	if (globalThis.window === undefined) return;
	if (isEmailLinkLandingUrl()) {
		globalThis.window.history.replaceState({}, document.title, '/');
	}
};

const linkDocumentRef = (linkId) => doc(db, 'links', linkId);
const categoryDocumentRef = (categoryId) => doc(db, 'categories', categoryId);

export const requestEmailSignInLink = async (rawEmail, addError) => {
	const email = String(rawEmail ?? '').trim();
	if (!email) {
		addError('Please enter your email address.');
		return {ok: false};
	}
	try {
		const sent = await deliverSignInLink(email, addError);
		return {ok: sent};
	} catch (error) {
		if (globalThis.window !== undefined) {
			globalThis.window.localStorage.removeItem(EMAIL_FOR_SIGNIN_STORAGE_KEY);
		}
		if (error.code === 'auth/invalid-email') {
			addError('Invalid email address');
		} else if (error.code === 'auth/configuration-not-found') {
			addError('Firebase Auth is not configured for this project/app. Check API key, Auth provider setup, and authorized domains.');
		} else if (error.code === 'auth/too-many-requests') {
			addError('Too many attempts. Please try again later.');
		} else {
			addError('Could not send sign-in email. Please try again.');
		}
		return {ok: false};
	}
};

const completeActionCodeFromExchange = async (exchanged, addError, options = {}) => {
	const {onSuccess} = options;
	const user = auth.currentUser;
	if (!user) {
		addError('Open this link in the same browser where you are signed in to LinkSpace.');
		return {ok: false, needsEmail: false};
	}
	try {
		await applyActionCode(auth, exchanged.oobCode);
		onSuccess?.();
		stripEmailLinkQueryFromCurrentUrl();
		if (exchanged.mode === AUTH_ACTION_MODES.verifyAndChangeEmail) {
			clearPendingEmailChange();
			return {ok: true, needsEmail: false, action: 'emailChange'};
		}
		return {ok: true, needsEmail: false, action: 'verifyEmail'};
	} catch (error) {
		if (error.code === 'auth/expired-action-code' || error.code === 'auth/invalid-action-code') {
			addError('This link is no longer valid. Request a new one and try again.');
		} else if (error.code === 'auth/user-disabled') {
			addError('This account has been disabled.');
		} else {
			addError('Could not complete this request. Please try again.');
		}
		return {ok: false, needsEmail: false};
	}
};

const completeSignInWithEmailLinkHref = async (href, rawEmailInput, addError, options = {}) => {
	const {onSuccess} = options;
	const trimmedInput = String(rawEmailInput ?? '').trim();
	const stored = globalThis.window?.localStorage.getItem(EMAIL_FOR_SIGNIN_STORAGE_KEY);
	const email = trimmedInput || (stored ? String(stored).trim() : '');
	if (!email) {
		return {ok: false, needsEmail: true};
	}
	try {
		const user = auth.currentUser;
		let didReauthenticate = false;
		if (user?.email?.toLowerCase() === email.toLowerCase()) {
			const credential = EmailAuthProvider.credentialWithLink(email, href);
			await reauthenticateWithCredential(user, credential);
			didReauthenticate = true;
		} else {
			if (user) {
				await signOut(auth);
			}
			await signInWithEmailLink(auth, email, href);
		}
		onSuccess?.();
		globalThis.window.localStorage.removeItem(EMAIL_FOR_SIGNIN_STORAGE_KEY);
		stripEmailLinkQueryFromCurrentUrl();
		const deletionOccurred = await resumePendingAccountDeletion(addError);
		if (didReauthenticate) {
			await resumePendingEmailAddressChange(addError);
		}
		try {
			const channel = new BroadcastChannel('linkspace-auth');
			channel.postMessage({type: deletionOccurred ? 'ACCOUNT_DELETED' : 'SIGNED_IN'});
			channel.close();
		} catch {}
		return {ok: true, needsEmail: false, action: deletionOccurred ? 'deletion' : 'signin'};
	} catch (error) {
		if (error.code !== 'auth/invalid-email') {
			globalThis.window.localStorage.removeItem(EMAIL_FOR_SIGNIN_STORAGE_KEY);
		}
		if (error.code === 'auth/invalid-email') {
			addError('Enter the exact email address that received this sign-in link.');
		} else if (error.code === 'auth/invalid-action-code' || error.code === 'auth/expired-action-code') {
			const signedInEmail = auth.currentUser?.email?.toLowerCase();
			if (signedInEmail && signedInEmail === email.toLowerCase()) {
				onSuccess?.();
				stripEmailLinkQueryFromCurrentUrl();
				return {ok: true, needsEmail: false, action: 'signin'};
			}
			addError(
				'This sign-in link is no longer valid. Request a new link, open it in the same browser, and use the same email.'
			);
		} else if (error.code === 'auth/credential-already-in-use') {
			addError('This email is already linked to another sign-in method.');
		} else {
			addError('Could not complete sign-in. Please try again.');
		}
		return {ok: false, needsEmail: false};
	}
};

export const completeEmailLinkSignIn = async (rawEmailInput, addError, options = {}) => {
	if (emailLinkSignInInFlight) {
		return emailLinkSignInInFlight;
	}

	emailLinkSignInInFlight = (async () => {
		const {onSuccess} = options;
		if (globalThis.window === undefined) {
			return {ok: false, needsEmail: false};
		}
		const href = globalThis.window.location.href;
		if (hasExtensionEmailLinkQueryParams(href) || isExtensionShortSignInUrl(href)) {
			return {ok: false, needsEmail: false};
		}

		const shortId = getShortSignInTokenFromHref(href);
		if (shortId) {
			try {
				if (shouldUseCustomAuthEmails()) {
					const peek = await peekShortSignInToken(shortId);
					if (
						peek.purpose === AUTH_EMAIL_PURPOSES.emailChangeConfirm
						&& peek.mode === AUTH_ACTION_MODES.verifyAndChangeEmail
					) {
						return completeEmailChangeViaToken(shortId, addError);
					}
					if (peek.purpose === AUTH_EMAIL_PURPOSES.emailChangeAlert) {
						return revertEmailChangeViaToken(shortId, addError);
					}
				}
				const exchanged = await exchangeShortSignInToken(shortId);
				if (exchanged.mode === AUTH_ACTION_MODES.verifyEmail) {
					return completeActionCodeFromExchange(exchanged, addError, {onSuccess});
				}
				const emailLinkHref = buildSignInEmailLinkHref(exchanged.oobCode, {
					extension: Boolean(exchanged.extension),
					mode: exchanged.mode || AUTH_ACTION_MODES.signIn
				});
				return completeSignInWithEmailLinkHref(emailLinkHref, rawEmailInput, addError, {onSuccess});
			} catch (error) {
				const code = String(error?.code ?? '');
				if (code === 'functions/not-found' || code === 'functions/failed-precondition') {
					addError('This sign-in link is no longer valid. Request a new link, open it in the same browser, and use the same email.');
				} else if (code === 'functions/invalid-argument') {
					addError('This sign-in link is invalid. Request a new link.');
				} else {
					addError('Could not complete sign-in. Please try again.');
				}
				return {ok: false, needsEmail: false};
			}
		}

		if (!isSignInWithEmailLink(auth, href)) {
			return {ok: false, needsEmail: false};
		}
		return completeSignInWithEmailLinkHref(href, rawEmailInput, addError, {onSuccess});
	})();

	try {
		return await emailLinkSignInInFlight;
	} finally {
		emailLinkSignInInFlight = null;
	}
};

export const logout = async (addError, {onBeforeSignOut} = {}) => {
	try {
		clearPendingEmailChange();
		clearPendingAccountDeletion();
		onBeforeSignOut?.();
		await signOut(auth);
		await clearFirestoreCache();
		return true;
	} catch (error) {
		addError(`Error while logging out: ${error.message}`);
	}
}

const sendEmailChangeReauthLink = async (user, addError) => {
	const currentEmail = String(user?.email ?? '').trim();
	if (!currentEmail) {
		addError('You must be logged in to update your email.');
		return false;
	}
	if (globalThis.window !== undefined) {
		globalThis.window.localStorage.setItem(EMAIL_FOR_SIGNIN_STORAGE_KEY, currentEmail);
	}
	try {
		const sent = await deliverSignInLink(currentEmail, addError, {
			purpose: AUTH_EMAIL_PURPOSES.emailChangeReauth
		});
		return sent;
	} catch (error) {
		if (globalThis.window !== undefined) {
			globalThis.window.localStorage.removeItem(EMAIL_FOR_SIGNIN_STORAGE_KEY);
		}
		if (error.code === 'auth/too-many-requests') {
			addError('Too many attempts. Please try again later.');
		} else {
			addError('Could not send sign-in email. Please try again.');
		}
		return false;
	}
};

export const resumePendingEmailAddressChange = async (addError) => {
	const pending = readPendingEmailChange();
	if (!pending) return false;

	const user = auth.currentUser;
	if (!user?.uid || user.uid !== pending.uid) return false;

	try {
		if (shouldUseCustomAuthEmails()) {
			await refreshAuthToken();
		}
		const result = await deliverEmailChangeConfirmation(pending.nextEmail, addError);
		if (!result.ok) return false;
		clearPendingEmailChange();
		addError('Confirmation sent to the new address. Open that email to finish updating your email.');
		return true;
	} catch (error) {
		if (error.code === 'auth/email-already-in-use') {
			clearPendingEmailChange();
			addError('Email is already in use');
			return false;
		}
		if (error.code === 'auth/invalid-email') {
			clearPendingEmailChange();
			addError('Invalid email address');
			return false;
		}
		if (error.code === 'auth/requires-recent-login') {
			addError('Please open the latest sign-in link from your current email, then retry.');
			return false;
		}
		addError(`Error while requesting email change: ${error.message}`);
		return false;
	}
};

export const resendVerificationEmail = async (addError) => {
	try {
		if (!auth.currentUser) {
			addError('You must be logged in to resend verification email.');
			return false;
		}
		const sent = await deliverEmailVerification(addError);
		if (!sent) return false;
		addError('Verification email sent. Please check your inbox.');
		return true;
	} catch (error) {
		addError(`Error while sending verification email: ${error.message}`);
		return false;
	}
};

export const requestEmailAddressChange = async (nextEmail, addError) => {
	const user = auth.currentUser;
	const sanitizedNextEmail = String(nextEmail ?? '').trim();

	try {
		if (!user?.email) {
			addError('You must be logged in to update your email.');
			return false;
		}

		if (!sanitizedNextEmail) {
			addError('New email is required.');
			return false;
		}

		const currentEmail = String(user.email).trim().toLowerCase();
		if (sanitizedNextEmail.toLowerCase() === currentEmail) {
			addError('New email must be different from current email.');
			return false;
		}

		const result = await deliverEmailChangeConfirmation(sanitizedNextEmail, addError);
		if (result.ok) {
			clearPendingEmailChange();
			addError('Confirmation sent to the new address. Open that email to finish updating your email.');
			return true;
		}
		if (result.reason === 'requires-recent-login') {
			writePendingEmailChange(user.uid, sanitizedNextEmail);
			const sent = await sendEmailChangeReauthLink(user, addError);
			if (sent) {
				addError(
					'For security, we sent a sign-in link to your current email. Open it in this browser to continue your email change.'
				);
			} else {
				clearPendingEmailChange();
			}
			return false;
		}
		return false;
	} catch (error) {
		if (error.code === 'auth/invalid-email') {
			addError('Invalid email address');
			return false;
		}
		if (error.code === 'auth/email-already-in-use') {
			addError('Email is already in use');
			return false;
		}
		if (error.code === 'auth/requires-recent-login') {
			if (!user?.uid) {
				addError('Please sign in again with your email link, then retry.');
				return false;
			}
			writePendingEmailChange(user.uid, sanitizedNextEmail);
			const sent = await sendEmailChangeReauthLink(user, addError);
			if (sent) {
				addError(
					'For security, we sent a sign-in link to your current email. Open it in this browser to continue your email change.'
				);
			} else {
				clearPendingEmailChange();
			}
			return false;
		}
		if (error.code === 'auth/unauthorized-continue-uri') {
			addError('This site is not authorized for email actions. Add your domain under Firebase Authentication → Settings → Authorized domains.');
			return false;
		}
		if (error.code === 'auth/operation-not-allowed') {
			addError('Email change is not enabled for this project. Enable Email/Password (with email link sign-in) in Firebase Authentication.');
			return false;
		}
		addError(`Error while requesting email change: ${error.message}`);
		return false;
	}
};

export const uploadUserAvatar = async (imageFile, addError) => {
	try {
		const user = auth.currentUser;
		if (!user?.uid) {
			addError('You must be logged in to upload an avatar.');
			return null;
		}
		if (!isSupportedAvatarImageFormat(imageFile)) {
			addError('Unsupported avatar format. Use JPG, PNG, or WEBP.');
			return null;
		}
		const optimizedImage = await optimizeAvatarImage(imageFile);
		const path = buildAvatarStoragePath(user.uid, optimizedImage);
		const avatarRef = ref(storage, path);
		const snapshot = await uploadBytes(avatarRef, optimizedImage);
		const photoURL = await getDownloadURL(snapshot.ref);
		await updateAuthProfile(user, {photoURL});
		return photoURL;
	} catch (error) {
		addError(`Error while uploading avatar: ${error.message}`);
		return null;
	}
};

const getAvatarSortKey = (downloadUrl) => {
	const storagePath = getStoragePathFromDownloadUrl(downloadUrl);
	if (!storagePath) return 0;
	const fileName = storagePath.split('/').pop() ?? '';
	const timestampMatch = fileName.match(/^(\d+)/);
	return timestampMatch ? Number(timestampMatch[1]) : 0;
};

export const fetchUserAvatars = async (addError) => {
	try {
		const ownerUid = getCurrentUserUid(addError, 'fetch avatars');
		if (!ownerUid) return [];
		const prefixes = [getUserAvatarPath(ownerUid), `user_avatars/${ownerUid}`];
		const urls = [];
		for (const prefix of prefixes) {
			try {
				const listRef = ref(storage, prefix);
				const result = await listAll(listRef);
				const urlPromises = result.items.map(async (itemRef) => {
					try {
						return await getDownloadURL(itemRef);
					} catch {
						return null;
					}
				});
				const prefixUrls = (await Promise.all(urlPromises)).filter((url) => url !== null);
				urls.push(...prefixUrls);
			} catch {
			}
		}
		return [...new Set(urls)].sort((a, b) => getAvatarSortKey(b) - getAvatarSortKey(a));
	} catch {
		return [];
	}
};

export const selectUserAvatar = async (photoURL, addError) => {
	try {
		const user = auth.currentUser;
		if (!user?.uid) {
			addError('You must be logged in to select an avatar.');
			return null;
		}
		if (photoURL === default_avatar) {
			await updateAuthProfile(user, {photoURL: ''});
			return default_avatar;
		}
		const storagePath = getStoragePathFromDownloadUrl(photoURL);
		if (
			!storagePath
			|| (
				!isUserScopedAvatarPath(storagePath, user.uid)
				&& !isLegacyAvatarPath(storagePath, user.uid)
				&& !isGlobalDefaultAvatarPath(storagePath)
			)
		) {
			addError('Invalid avatar selection.');
			return null;
		}
		await updateAuthProfile(user, {photoURL});
		return photoURL;
	} catch (error) {
		addError(`Error while selecting avatar: ${error.message}`);
		return null;
	}
};

const collectStorageRefs = async (prefix) => {
	const refsByPath = new Map();
	try {
		const listRef = ref(storage, prefix);
		const result = await listAll(listRef);
		result.items.forEach((itemRef) => {
			if (itemRef.fullPath) {
				refsByPath.set(itemRef.fullPath, itemRef);
			}
		});
		await Promise.all(result.prefixes.map(async (subfolderRef) => {
			const nestedRefs = await collectStorageRefs(subfolderRef.fullPath);
			nestedRefs.forEach((itemRef, fullPath) => {
				refsByPath.set(fullPath, itemRef);
			});
		}));
	} catch (error) {
		if (refsByPath.size === 0) {
			throw error;
		}
	}
	return refsByPath;
};

const deleteStorageRefs = async (refsByPath) => {
	const errors = [];
	await Promise.all([...refsByPath.values()].map(async (itemRef) => {
		try {
			await deleteObject(itemRef);
		} catch (error) {
			errors.push(error);
		}
	}));
	if (errors.length > 0) {
		throw errors[0];
	}
};

const deleteUserAvatarFiles = async (userUid) => {
	if (!userUid) return;
	const refsByPath = await collectStorageRefs(getUserAvatarPath(userUid));
	await deleteStorageRefs(refsByPath);
};

const deleteUserStorageTree = async (userUid) => {
	if (!userUid) return;
	const prefixes = [getUserStorageRoot(userUid), `user_avatars/${userUid}`];
	const refsByPath = new Map();
	for (const prefix of prefixes) {
		try {
			const refs = await collectStorageRefs(prefix);
			refs.forEach((itemRef, fullPath) => {
				refsByPath.set(fullPath, itemRef);
			});
		} catch {
		}
	}
	if (refsByPath.size > 0) {
		await deleteStorageRefs(refsByPath);
	}
};

const copyStorageAsset = async (downloadUrl, destinationPath) => {
	const response = await fetch(downloadUrl);
	if (!response.ok) {
		throw new Error(`Failed to fetch storage asset (${response.status})`);
	}
	const blob = await response.blob();
	const destinationRef = ref(storage, destinationPath);
	await uploadBytes(destinationRef, blob);
	return getDownloadURL(destinationRef);
};

const migrateLinkImage = async (link, userId) => {
	const ext = getExtensionFromPath(getStoragePathFromDownloadUrl(link.image));
	const destinationPath = buildLinkImageStoragePath(
		userId,
		link.name,
		{name: `migrated.${ext}`, type: `image/${ext}`}
	);
	return copyStorageAsset(link.image, destinationPath);
};

const migrateAvatarImage = async (photoURL, userId) => {
	const ext = getExtensionFromPath(getStoragePathFromDownloadUrl(photoURL));
	const destinationPath = buildAvatarStoragePath(
		userId,
		{name: `migrated.${ext}`, type: `image/${ext}`}
	);
	return copyStorageAsset(photoURL, destinationPath);
};

export const removeUserAvatar = async (photoURL, addError) => {
	try {
		const user = auth.currentUser;
		if (!user) {
			addError('You must be logged in to remove avatar.');
			return false;
		}
		if (!canRemoveAvatarUrl(photoURL, user.uid)) {
			addError('This avatar cannot be removed.');
			return false;
		}

		const storagePath = getStoragePathFromDownloadUrl(photoURL);
		const avatarRef = ref(storage, storagePath);
		await deleteObject(avatarRef);

		if ((user.photoURL ?? '') === photoURL) {
			await updateAuthProfile(user, {photoURL: ''});
		}
		return true;
	} catch (error) {
		addError(`Error while removing avatar: ${error.message}`);
		return false;
	}
};

const deleteOwnerDocuments = async (ownerUid) => {
	const collectionsToClean = ["links", "categories", "profiles"];
	const snapshots = await Promise.all(collectionsToClean.map((collectionName) => (
		getDocs(query(
			collection(db, collectionName),
			where('ownerUid', '==', ownerUid)
		))
	)));
	const refs = snapshots.flatMap((snapshot) => snapshot.docs.map((item) => item.ref));
	if (refs.length > 0) {
		const batches = chunkArray(refs, 450);
		for (const refsChunk of batches) {
			const batch = writeBatch(db);
			refsChunk.forEach((docRef) => batch.delete(docRef));
			await batch.commit();
		}
	}
	return true;
};

const deleteOwnerStorageFiles = async (user) => {
	await deleteUserStorageTree(user?.uid);
};

const deliverAccountDeletionReauth = async (email, addError) => (
	deliverSignInLink(email, addError, {purpose: AUTH_EMAIL_PURPOSES.accountDeletion})
);

export const deleteCurrentUserAccount = async (addError) => {
	const user = auth.currentUser;
	if (!user?.uid) {
		addError('You must be logged in to delete your account.');
		return false;
	}
	const email = String(user.email ?? '').trim();
	if (!email) {
		addError('You must be logged in to delete your account.');
		return false;
	}
	writePendingAccountDeletion(user.uid);
	const sent = await deliverAccountDeletionReauth(email, addError);
	if (sent) {
		addError('Verification link sent — check your email to confirm account deletion.');
		return 'reauthLinkSent';
	}
	clearPendingAccountDeletion();
	return false;
};

export const resumePendingAccountDeletion = async (addError) => {
	const pending = readPendingAccountDeletion();
	if (!pending) return false;

	const user = auth.currentUser;
	if (!user?.uid || user.uid !== pending.uid) return false;

	clearPendingAccountDeletion();
	try {
		const ownerUid = user.uid;
		await deleteOwnerDocuments(ownerUid);
		await deleteOwnerStorageFiles(user);
		await deleteUser(user);
		addError('Account deleted successfully.');
		return true;
	} catch (error) {
		addError(`Error while deleting account: ${error.message}`);
		return false;
	}
};

export const fetchLinks = async (addError) => {
	try {
		const ownerUid = getCurrentUserUid(addError, 'fetch links');
		if (!ownerUid) return [];
		const querySnapshot = await getDocs(query(
			collection(db, 'links'),
			where('ownerUid', '==', ownerUid)
		));
		return toArray(querySnapshot);
	} catch (error) {
		addError(`Error while getting Links from Firebase: ${error.message}`)
		return [];
	}
}

export const updateLink = async (linkId, linkData, addError) => {
	try {
		const ownerUid = getCurrentUserUid(addError, 'update links');
		if (!ownerUid) return false;
		if (linkData?.name !== undefined
			&& !validateFieldLength(linkData.name, MAX_LINK_NAME_LENGTH, 'Link name', addError)) {
			return false;
		}
		if (linkData?.category !== undefined
			&& !validateFieldLength(linkData.category, MAX_CATEGORY_NAME_LENGTH, 'Category', addError)) {
			return false;
		}
		if (linkData?.url !== undefined
			&& !validateFieldLength(linkData.url, MAX_LINK_URL_LENGTH, 'URL', addError)) {
			return false;
		}
		const linkRef = linkDocumentRef(linkId);
		const payload = {...linkData};
		delete payload.ownerUid;
		await updateDoc(linkRef, payload);
		return true;
	} catch (error) {
		addError(`Error while updating Link in Firebase: ${error.message}`);
		return false;
	}
};

export const migrateUserStorage = async (addError) => {
	const user = auth.currentUser;
	if (!user?.uid) return false;

	try {
		if (user.photoURL && needsAvatarMigration(user.photoURL, user.uid)) {
			const photoURL = await migrateAvatarImage(user.photoURL, user.uid);
			await updateAuthProfile(user, {photoURL});
		}

		const links = await fetchLinks(addError);
		for (const link of links) {
			if (!link?.image || !link?.name) continue;
			if (!needsLinkImageMigration(link.image, user.uid)) continue;
			const image = await migrateLinkImage(link, user.uid);
			await updateLink(link.id, {image}, addError);
		}
		return true;
	} catch (error) {
		addError(`Error while migrating storage assets: ${error.message}`);
		return false;
	}
};

export const getDefaultAvatarUrl = async () => default_avatar;

export const uploadDefaultImage = async (addError) => {
	const storageRef = ref(storage, GLOBAL_DEFAULT_LINK_IMAGE_PATH);

	try {
		return await getDownloadURL(storageRef);
	} catch (error) {
		if (error?.code !== 'storage/object-not-found') {
			addError(`Could not load default image from Storage: ${error.message}`);
			return default_img;
		}
	}

	try {
		const response = await fetch(default_img);
		const blob = await response.blob();
		await uploadBytes(storageRef, blob);
		return await getDownloadURL(storageRef);
	} catch (error) {
		addError(`Error while uploading default Image to Firebase: ${error.message}`);
		return default_img;
	}
};

export const uploadNewImage = async (image, linkName, addError) => {
	try {
		const ownerUid = getCurrentUserUid(addError, 'upload link images');
		if (!ownerUid) return null;
		const normalizedLinkName = String(linkName ?? '').trim();
		if (!normalizedLinkName) {
			addError('Link name is required to upload an image.');
			return null;
		}
		if (!isSupportedLinkImageFormat(image)) {
			addError('Unsupported image format. Please upload a JPEG or PNG.');
			return null;
		}
		const optimizedImage = await optimizeLinkImage(image);
		const path = buildLinkImageStoragePath(ownerUid, normalizedLinkName, optimizedImage);
		const storageRef = ref(storage, path);
		const metadata = { contentType: optimizedImage?.type || image?.type || 'image/png' };
		const snapshot = await uploadBytes(storageRef, optimizedImage, metadata);
		return await getDownloadURL(snapshot.ref);
	} catch (error) {
		addError(`Error while uploading new Image to Firebase: ${error.message}`)
	}
}

export const fetchAllStorageImages = async (addError) => {
	try {
		const ownerUid = getCurrentUserUid(addError, 'fetch link images');
		if (!ownerUid) return [];
		const listRef = ref(storage, getUserLinkImagesPath(ownerUid));
		const result = await listAll(listRef);
		const urlPromises = result.items.map(async (itemRef) => {
			try {
				return await getDownloadURL(itemRef);
			} catch {
				return null;
			}
		});
		const urls = await Promise.all(urlPromises);
		return urls.filter(url => url !== null);
	} catch {
		return [];
	}
}

export const addLink = async (link, addError) => {
	try {
		const ownerUid = getCurrentUserUid(addError, 'add links');
		if (!ownerUid) return null;
		if (!validateFieldLength(link?.name, MAX_LINK_NAME_LENGTH, 'Link name', addError)) {
			return null;
		}
		if (!validateFieldLength(link?.category, MAX_CATEGORY_NAME_LENGTH, 'Category', addError)) {
			return null;
		}
		if (!validateFieldLength(link?.url, MAX_LINK_URL_LENGTH, 'URL', addError)) {
			return null;
		}
		const payload = {
			...link,
			ownerUid,
			profileIds: Array.from(new Set(getProfileIdsFromEntity(link))),
		};
		delete payload.profileId;
		return await addDoc(collection(db, 'links'), payload);
	} catch (error) {
		addError(`Error while adding new Link to Firebase: ${error.message}`)
	}
}

export const deleteLink = async (linkId, addError) => {
	const ownerUid = getCurrentUserUid(addError, 'remove links');
	if (!ownerUid) return false;
	const linkRef = linkDocumentRef(linkId);
	try {
		await deleteDoc(linkRef);
		return true;
	} catch (error) {
		addError(`Error while removing Link from Firebase: ${error.message}`);
		return false;
	}
}

export const fetchLinksByCategory = async (category, addError) => {
	try {
		const ownerUid = getCurrentUserUid(addError, 'fetch links by category');
		if (!ownerUid) return [];
		const querySnapshot = await getDocs(query(
			collection(db, 'links'),
			where('ownerUid', '==', ownerUid),
			where('category', '==', category)
		));
		return toArray(querySnapshot);
	} catch (error) {
		addError(`Error while fetching Links by Category from Firebase: ${error.message}`);
		return [];
	}
};

export const fetchCategories = async (addError) => {
	try {
		const ownerUid = getCurrentUserUid(addError, 'fetch categories');
		if (!ownerUid) return [];
		const querySnapshot = await getDocs(query(
			collection(db, 'categories'),
			where('ownerUid', '==', ownerUid)
		));
		return toArray(querySnapshot);
	} catch (error) {
		addError(`Error while getting Categories from Firebase: ${error.message}`);
		return [];
	}
}

export const fetchCategory = async (categoryId, addError) => {
	try {
		const ownerUid = getCurrentUserUid(addError, 'fetch categories');
		if (!ownerUid) return null;
		const snapshot = await getDoc(categoryDocumentRef(categoryId));
		if (!snapshot.exists()) return null;
		const data = snapshot.data();
		if (data?.ownerUid !== ownerUid) {
			addError('Category not found');
			return null;
		}
		return {id: snapshot.id, ...data};
	} catch (error) {
		addError(`Error while fetching Category in Firebase: ${error.message}`);
		return null;
	}
}

export const updateCategory = async (categoryObject, addError) => {
	if (!categoryObject?.id) {
		addError('Invalid category ID for update');
		return false;
	}

	if (!validateCategoryName(categoryObject?.category, addError)) {
		return false;
	}

	const categoryData = buildCategoryPayload(categoryObject);

	const categoryRef = categoryDocumentRef(categoryObject.id);
	try {
		await updateDoc(categoryRef, categoryData);
		return true;
	} catch (error) {
		addError(`Error while updating Category in Firebase: ${error.message}`);
		return false;
	}
};

export const addCategory = async (category, addError) => {
	try {
		const ownerUid = getCurrentUserUid(addError, 'add categories');
		if (!ownerUid) return null;
		if (!validateCategoryName(category?.category, addError)) {
			return null;
		}
		const categoryData = buildCategoryPayload(category);
		categoryData.ownerUid = ownerUid;
		return await addDoc(collection(db, "categories"), categoryData);
	} catch (error) {
		addError(`Error while uploading new Category to Firebase: ${error.message}`);
	}
}

export const uploadNewCategory = addCategory;

export const deleteCategory = async (categoryId, addError) => {
	const ownerUid = getCurrentUserUid(addError, 'remove categories');
	if (!ownerUid) return false;
	const categoryRef = categoryDocumentRef(categoryId);
	try {
		await deleteDoc(categoryRef);
		return true;
	} catch (error) {
		addError(`Error while removing Category from Firebase: ${error.message}`);
		return false;
	}
}

export const addProfile = async (profile, addError) => {
	try {
		const ownerUid = getCurrentUserUid(addError, 'add profiles');
		if (!ownerUid) return null;
		const profileName = String(profile?.profileName ?? "").trim().toUpperCase();
		if (!profileName) {
			addError("Profile name is required");
			return null;
		}
		if (profileName === RESERVED_PROFILE_NAME) {
			addError(`Profile "${RESERVED_PROFILE_NAME}" is reserved`);
			return null;
		}
		if (!validateProfileName(profileName, addError)) {
			return null;
		}
		return await addDoc(collection(db, "profiles"), {profileName, ownerUid});
	} catch (error) {
		addError(`Error while adding new Profile to Firebase: ${error.message}`);
		return null;
	}
};

export const updateProfile = async (profileId, profileData, addError) => {
	try {
		const ownerUid = getCurrentUserUid(addError, 'update profiles');
		if (!ownerUid) return false;
		if (!profileId || profileId === VIRTUAL_ALL_PROFILE_ID) {
			addError("Invalid profile ID for update");
			return false;
		}
		const profileName = String(profileData?.profileName ?? "").trim().toUpperCase();
		if (!profileName) {
			addError("Profile name is required");
			return false;
		}
		if (profileName === RESERVED_PROFILE_NAME) {
			addError(`Profile "${RESERVED_PROFILE_NAME}" is reserved`);
			return false;
		}
		if (!validateProfileName(profileName, addError)) {
			return false;
		}
		const profileRef = doc(db, "profiles", profileId);
		await updateDoc(profileRef, {
			profileName
		});
		return true;
	} catch (error) {
		addError(`Error while updating Profile in Firebase: ${error.message}`);
		return false;
	}
};

export const fetchProfiles = async (addError) => {
	try {
		const ownerUid = getCurrentUserUid(addError, 'fetch profiles');
		if (!ownerUid) return [];
		const querySnapshot = await getDocs(query(
			collection(db, "profiles"),
			where('ownerUid', '==', ownerUid)
		));
		return toArray(querySnapshot).filter((profile) => (
			String(profile.profileName ?? "").trim().toUpperCase() !== RESERVED_PROFILE_NAME
		));
	} catch (error) {
		addError(`Error while getting Profiles from Firebase: ${error.message}`);
		return [];
	}
};

/**
 * Subscribe to the current user's links with an onSnapshot listener.
 * Returns the unsubscribe function — call it on logout or component unmount.
 */
export const subscribeLinks = (ownerUid, onUpdate, onError) => {
	if (!ownerUid) return () => {};
	return onSnapshot(
		query(collection(db, 'links'), where('ownerUid', '==', ownerUid)),
		(snapshot) => onUpdate(toArray(snapshot)),
		onError
	);
};

/**
 * Subscribe to the current user's categories with an onSnapshot listener.
 * Returns the unsubscribe function — call it on logout or component unmount.
 */
export const subscribeCategories = (ownerUid, onUpdate, onError) => {
	if (!ownerUid) return () => {};
	return onSnapshot(
		query(collection(db, 'categories'), where('ownerUid', '==', ownerUid)),
		(snapshot) => onUpdate(toArray(snapshot)),
		onError
	);
};

/**
 * Subscribe to the current user's profiles with an onSnapshot listener.
 * Returns the unsubscribe function — call it on logout or component unmount.
 */
export const subscribeProfiles = (ownerUid, onUpdate, onError) => {
	if (!ownerUid) return () => {};
	return onSnapshot(
		query(collection(db, 'profiles'), where('ownerUid', '==', ownerUid)),
		(snapshot) => onUpdate(toArray(snapshot)),
		onError
	);
};

export const deleteProfile = async (profileId, addError) => {
	try {
		const ownerUid = getCurrentUserUid(addError, 'remove profiles');
		if (!ownerUid) return false;
		if (!profileId || profileId === VIRTUAL_ALL_PROFILE_ID) {
			addError("Invalid profile ID for removal");
			return false;
		}
		await deleteDoc(doc(db, "profiles", profileId));
		return true;
	} catch (error) {
		addError(`Error while removing Profile from Firebase: ${error.message}`);
		return false;
	}
};

export const deleteProfileLifecycle = async (profileId, addError) => {
	const ownerUid = getCurrentUserUid(addError, 'remove profile lifecycle');
	if (!ownerUid) return false;
	if (!profileId || profileId === VIRTUAL_ALL_PROFILE_ID) {
		addError("Invalid profile ID for lifecycle deletion");
		return false;
	}

	try {
		const profileSnapshot = await getDoc(doc(db, "profiles", profileId));
		if (!profileSnapshot.exists() || profileSnapshot.data()?.ownerUid !== ownerUid) {
			addError('Profile not found');
			return false;
		}

		const [linksSnapshot, categoriesSnapshot] = await Promise.all([
			getDocs(query(collection(db, "links"), where('ownerUid', '==', ownerUid))),
			getDocs(query(collection(db, "categories"), where('ownerUid', '==', ownerUid))),
		]);

		const batch = writeBatch(db);
		linksSnapshot.docs.forEach((snapshot) => {
			const link = {id: snapshot.id, ...snapshot.data()};
			const profileIds = getProfileIdsFromEntity(link);
			if (!profileIds.includes(profileId)) return;

			const remaining = profileIds.filter((id) => id !== profileId);
			if (remaining.length === 0) {
				batch.delete(snapshot.ref);
			} else {
				batch.update(snapshot.ref, {profileIds: remaining});
			}
		});

		categoriesSnapshot.docs.forEach((snapshot) => {
			const category = {id: snapshot.id, ...snapshot.data()};
			const profileIds = getProfileIdsFromEntity(category);
			if (!profileIds.includes(profileId) && !profileIds.includes(VIRTUAL_ALL_PROFILE_ID)) return;

			if (profileIds.includes(VIRTUAL_ALL_PROFILE_ID)) {
				return;
			}

			const nextProfileIds = profileIds.filter((id) => id !== profileId);
			const idxPerProfile = category.idxPerProfile;
			const nextIdxPerProfile = idxPerProfile ? {...idxPerProfile} : {};
			delete nextIdxPerProfile[profileId];

			if (nextProfileIds.length === 0) {
				batch.delete(snapshot.ref);
			} else {
				batch.update(snapshot.ref, {
					profileIds: nextProfileIds,
					idxPerProfile: nextIdxPerProfile,
				});
			}
		});

		batch.delete(doc(db, "profiles", profileId));
		await batch.commit();
		return true;
	} catch (error) {
		addError(`Error while removing profile lifecycle in Firebase: ${error.message}`);
		return false;
	}
};

const getCallableErrorMessage = (error) => {
	if (typeof error?.message === "string" && error.message.trim()) {
		return error.message;
	}
	return "Failed to submit bug report.";
};

export const submitBugReport = async ({title, description}, addError) => {
	if (!auth.currentUser) {
		addError("You must be signed in to submit a report.");
		return {ok: false, error: "You must be signed in to submit a report."};
	}

	try {
		const submitReport = httpsCallable(functions, "submitBugReport");
		const response = await submitReport({
			title,
			description,
			appEnv: process.env.REACT_APP_ENV ?? "unknown"
		});
		addError("Bug report submitted");
		return {ok: true, issueUrl: response?.data?.issueUrl};
	} catch (error) {
		const message = getCallableErrorMessage(error);
		addError(message);
		return {ok: false, error: message};
	}
};

export const cleanUpDataBase = async (addError) => {
	if (!isDestructiveCleanupAllowed()) {
		addError('Database cleanup blocked: allowed only with emulator + explicit cleanup flag + demo project.');
		return false;
	}

	const links = await fetchLinks(addError);
	if (links?.length) {
		await Promise.all(links.map((link) => deleteLink(link.id, addError)));
	} else {
		addError(`No links found to be removed from Database`);
	}

	const categories = await fetchCategories(addError)
	if (categories?.length) {
		await Promise.all(categories.map((category) => deleteCategory(category.id, addError)));
	} else {
		addError(`No categories found to be removed from DataBase`);
	}

	return true;
}
